import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { bankApi, invApi, todayApi, vouchersApi, type BaseType, type DayBookRow } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad } from "../ui/useLoad";
import { fmtAmount } from "../lib/money";
import { SECTIONS } from "./nav";
import { toUrl } from "./routes";
import { docTitle } from "../vouchers/docs";
import { PendingOrders } from "../orders/Orders";
import { RecurringBox } from "../recurring/Recurring";

const rupees = (n: number | null | undefined) => `₹ ${fmtAmount(Math.round(n ?? 0))}`.replace(/\.00$/, "");
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const short = (s: string) => new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
const STATE: Record<string, string> = { filed: "Filed", overdue: "Overdue", due: "Due", ready: "Ready to file", upcoming: "Coming up" };

// A section's home: what it is for, its figures and latest work. Its pages are in the menu.
export function SectionHub() {
  const { sectionId } = useParams();
  const s = SECTIONS.find((x) => x.id === sectionId);
  if (!s) return <section className="page"><h1>Not found</h1><p className="muted">There's no page at this address.</p></section>;
  const Icon = s.icon!;
  return (
    <section className="page hub">
      <header className="hub-head">
        <div className="hub-icon" aria-hidden="true"><Icon size={22} strokeWidth={1.7} /></div>
        <div><h1>{s.title} <span className="code-badge">{s.code}</span></h1><p className="muted">{s.desc}</p></div>
      </header>

      <Live section={s.id} />

    </section>
  );
}

// the live part of each section's home
function Live({ section }: { section: string }) {
  const { company } = useCompany();
  const t = new Date();
  const from = iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to = iso(t), m0 = iso(new Date(t.getFullYear(), t.getMonth(), 1));
  const needsToday = ["sales", "purchases", "compliance", "accounting"].includes(section);
  const today = useLoad(() => (needsToday ? todayApi.get(company.id) : Promise.resolve(null)), [company.id, section]);
  const book = useLoad(() => (["sales", "purchases", "accounting", "banking"].includes(section)
    ? vouchersApi.dayBook(company.id, from, to).catch(() => [] as DayBookRow[]) : Promise.resolve([] as DayBookRow[])), [company.id, section]);
  const banks = useLoad(() => (section === "banking" ? bankApi.accounts(company.id) : Promise.resolve(null)), [company.id, section]);
  const items = useLoad(() => (section === "inventory" ? invApi.items(company.id) : Promise.resolve(null)), [company.id, section]);
  const rows = book.data ?? [];
  const of = (b: BaseType[]) => rows.filter((r) => b.includes(r.base_type) && r.status !== "voided");
  const month = (b: BaseType[]) => of(b).filter((r) => r.status === "posted" && r.date >= m0).reduce((x, r) => x + r.amount, 0);
  const f = today.data?.figures;

  const Recent = ({ list, empty }: { list: DayBookRow[]; empty: string }) => (
    <section className="card today-box grow3">
      <header><h2>Latest</h2><Link className="small" to={toUrl("vouchers/daybook")}>All transactions <span className="code">JR01</span></Link></header>
      {list.length === 0 ? <p className="muted pad">{empty}</p> :
        <table className="grid-table"><tbody>{list.slice(-6).reverse().map((r) => (
          <tr key={r.id}><td className="muted">{short(r.date)}</td>
            <td><Link to={toUrl(`vouchers/v/${r.id}`)}>{r.number ?? "draft"}</Link></td>
            <td>{docTitle({ name: r.type, base_type: r.base_type })}</td><td>{r.particulars ?? ""}</td>
            <td className="num">{fmtAmount(r.amount)}</td></tr>))}</tbody></table>}
    </section>
  );

  if (section === "sales") return (
    <>
      <div className="kpis">
        <Kpi label="Sales this month" value={rupees(month(["sales"]) - month(["credit_note"]))} sub="Invoices less credit notes, with tax" />
        <Kpi label="Customers owe" value={rupees(f?.receivables)} sub={f?.receivables_overdue ? `${rupees(f.receivables_overdue)} overdue past 30 days` : "Nothing overdue past 30 days"} />
        <Kpi label="Received this month" value={rupees(month(["receipt"]))} sub="Receipts posted" />
      </div>
      <div className="today-cols"><Recent list={of(["sales", "credit_note", "receipt"])} empty="No sales in the last two months. Start with New Invoice (SI01)." /><PendingOrders kind="sales" /><RecurringBox kind="sales" /></div>
    </>
  );
  if (section === "purchases") return (
    <>
      <div className="kpis">
        <Kpi label="Bought this month" value={rupees(month(["purchase"]) - month(["debit_note"]))} sub="Bills less debit notes, with tax" />
        <Kpi label="You owe suppliers" value={rupees(f?.payables)} sub={f?.payables_due_week ? `${rupees(f.payables_due_week)} due within a week` : "Nothing due within a week"} />
        <Kpi label="Paid this month" value={rupees(month(["payment"]))} sub="Payments posted" />
      </div>
      <div className="today-cols"><Recent list={of(["purchase", "debit_note", "payment"])} empty="No purchases in the last two months. Start with New Bill (PB01)." /><PendingOrders kind="purchase" /><RecurringBox kind="purchase" /></div>
    </>
  );
  if (section === "banking") {
    const a = banks.data?.accounts ?? [];
    return (
      <div className="kpis">
        {a.length === 0 ? <div className="card kpi"><div className="kpi-label">Bank accounts</div><div className="kpi-sub muted">None yet: open an account under Bank accounts (1320) with its bank details.</div></div>
          : a.map((b) => (
            <Link key={b.ledger_id} to={toUrl(`banking/reconcile?ledger=${b.ledger_id}`)} className="card kpi link-card">
              <div className="kpi-label">{b.name}</div><div className="kpi-value">{rupees(b.books_balance)}</div>
              <div className="kpi-sub muted">{b.unmatched ? `${b.unmatched} line${b.unmatched > 1 ? "s" : ""} to match` : "All matched"}
                {b.statement_balance != null ? ` · bank says ${rupees(b.statement_balance)}` : ""}{b.last_line ? ` on ${short(b.last_line)}` : ""}</div>
            </Link>))}
      </div>
    );
  }
  if (section === "inventory") {
    const v = (items.data ?? []).flatMap((i) => i.variants ?? []);
    return (
      <div className="kpis">
        <Kpi label="Items" value={String((items.data ?? []).filter((i) => i.active).length)} sub={`${v.length} variant${v.length === 1 ? "" : "s"}`} />
        <Kpi label="In stock" value={String(v.filter((x) => x.on_hand > 0).length)} sub="Variants with stock on hand" />
        <Kpi label="Below zero" value={String(v.filter((x) => x.on_hand < 0).length)} sub="Sold before the stock was entered" tone={v.some((x) => x.on_hand < 0) ? "bad" : undefined} />
      </div>
    );
  }
  if (section === "accounting") {
    const drafts = rows.filter((r) => r.status === "draft");
    return (
      <>
        <div className="kpis">
          <Kpi label="Entries this month" value={String(rows.filter((r) => r.status === "posted" && r.date >= m0).length)} sub="Posted, every kind" />
          <Kpi label="Drafts" value={String(drafts.length)} sub={drafts.length ? "Waiting to be posted" : "None waiting"} tone={drafts.length ? "warn" : undefined} />
          <Kpi label="Cash and bank" value={rupees(f?.cash_bank)} sub="In the books today" />
        </div>
        <div className="today-cols"><Recent list={of(["journal", "contra"])} empty="No journal entries in the last two months." /></div>
      </>
    );
  }
  if (section === "compliance") {
    const cal = today.data?.calendar ?? [];
    return (
      <div className="today-cols">
        <section className="card today-box grow3">
          <header><h2>Due and filed</h2><span className="muted small">Due dates as the law sets them</span></header>
          {cal.length === 0 ? <p className="muted pad">Add a GSTIN or TDS details under Settings (ST01) and the returns show here.</p>
            : cal.map((c, i) => (
              <Link key={i} to={toUrl(c.path)} className="due">
                <div className="date"><b>{new Date(c.date + "T00:00:00").getDate()}</b><span>{new Date(c.date + "T00:00:00").toLocaleDateString("en-IN", { month: "short" })}</span></div>
                <div className="what"><div className="t">{c.title}</div><div className="muted small">{c.detail}</div></div>
                <span className={`pill state-${c.state}`}>{c.state === "filed" && c.filed_on ? `Filed ${short(c.filed_on)}` : STATE[c.state]}</span>
              </Link>))}
        </section>
      </div>
    );
  }
  if (section === "reports") return <Periods />;
  return null;
}

// the main statements for the usual periods, one click each
function Periods() {
  const t = new Date();
  const fy = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
  const q0 = new Date(fy, 3 + Math.floor(((t.getMonth() + 9) % 12) / 3) * 3, 1);
  const P = useMemo(() => [
    { l: "This month", from: iso(new Date(t.getFullYear(), t.getMonth(), 1)), to: iso(t) },
    { l: "Last month", from: iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: iso(new Date(t.getFullYear(), t.getMonth(), 0)) },
    { l: "This quarter", from: iso(q0), to: iso(t) },
    { l: `FY ${fy}–${String((fy + 1) % 100).padStart(2, "0")}`, from: `${fy}-04-01`, to: iso(t) },
  ], [fy]);   // eslint-disable-line react-hooks/exhaustive-deps
  const S: [string, string][] = [["trial_balance", "Trial balance"], ["profit_loss", "Profit and loss"], ["balance_sheet", "Balance sheet"]];
  return (
    <section className="card today-box">
      <header><h2>Quick statements</h2><span className="muted small">Or type the code with a period: FS02 q2</span></header>
      <table className="grid-table periods"><tbody>{S.map(([name, title]) => (
        <tr key={name}><th>{title}</th>{P.map((p) => <td key={p.l}><Link to={toUrl(`reports/r/${name}?from=${p.from}&to=${p.to}&as_on=${p.to}`)}>{p.l}</Link></td>)}</tr>))}</tbody></table>
    </section>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "bad" | "warn" }) {
  return <div className={`card kpi${tone ? ` kpi-${tone}` : ""}`}><div className="kpi-label">{label}</div><div className="kpi-value">{value}</div><div className="kpi-sub muted">{sub}</div></div>;
}
